import {
  HotelDetail,
  Image,
  Request,
  RoomOffer,
  type SearchData,
} from "@/app/utils/apis/types";
import { isArray, isEmpty } from "lodash";
import determineRoomOccupancy from "@/app/utils/strings/determineRoomOccupancy";
import { getDropDownItems } from "@/app/common/consts/offers";
import type { Filters, SearchResultRow } from "./types";
import { mapCurrencyRounding } from "@/app/utils/numbers/currencyRounding";
import { useTranslations } from "next-intl";

const hasElements = <T>(arr: T[]): boolean => isArray(arr) && !isEmpty(arr);

const findCheapestOffer = (roomOffers: RoomOffer[]): RoomOffer | null => {
  return roomOffers
    .filter(({ priceDetails }) => priceDetails && priceDetails.total != null)
    .reduce((cheapest: RoomOffer | null, current: RoomOffer) => {
      if (cheapest === null) return current;

      if (current.priceDetails.total < cheapest.priceDetails.total) {
        return current;
      }

      return cheapest;
    }, null);
};

const HOTEL_AMMENTIES = [
  "hasEVChargingStation",
  "hasGym",
  "hasParking",
  "hasSpa",
  "isPetFriendly",
];

export const getHotelTotalScore = (
  hotelDetail: HotelDetail,
  request: Request
) => {
  let score = 0;

  const { userRatings, rating, hasFamilyRooms } = hotelDetail || {};

  const { numberOfChildren } = request;

  hotelDetail &&
    HOTEL_AMMENTIES.forEach((amenity: string) => {
      if ((hotelDetail as any)[amenity]) {
        score += 1;
      }
    });

  if (
    userRatings &&
    userRatings.length > 0 &&
    userRatings[0]?.numberOfReviews
  ) {
    score += userRatings[0].numberOfReviews / 100;
  }

  if (rating) {
    score += rating;
  }

  if (numberOfChildren && hasFamilyRooms) {
    score += 1;
  }

  return Number(score);
};

export const filterDataToDisplayFormat = (
  t: ReturnType<typeof useTranslations>,
  searchData: SearchData,
  _rate: number
): SearchResultRow[] => {
  const { tokenId, offers, request } = searchData;
  const roomOccupancySummary = determineRoomOccupancy(t, request);

  return offers.flatMap(({ hotelOffers }) =>
    hotelOffers
      // .filter(({ hotelDetail }) => hasElements(hotelDetail.images))
      .map((hotelOffer) => {
        const bestRoomOfferWithImage = findCheapestOffer(hotelOffer.roomOffers);

        const {
          hasGym,
          isPetFriendly,
          hasSpa,
          hasParking,
          hasFamilyRooms,
          hasEVChargingStation,
          hotelStars,
          ratings,
          totalScore,
          latitude,
          longitude,
          giataId,
          address,
          image,
          averageReviewScore,
          reviewCount,
          distance,
        } = hotelOffer;

        if (bestRoomOfferWithImage === null) {
          return null;
        }

        const { priceDetails, boardDetails, name, pointsToBeEarned } =
          bestRoomOfferWithImage;
        const { total, currency, discountDetails } = priceDetails;
        const discountedAmount = discountDetails ? discountDetails.value : 0;
        const originalPrice = total + discountedAmount;
        // const totalScore = getHotelTotalScore(hotelOffer?.hotelDetail, request);

        return {
          tokenId,
          giataId,
          roomOccupancySummary,
          providerHotelId: hotelOffer.providerHotelId,
          hotelDetail: hotelOffer.hotelDetail || {},
          totalScore,
          hasGym,
          isPetFriendly,
          hasSpa,
          hasParking,
          hasFamilyRooms,
          hasEVChargingStation,
          hotelStars,
          ratings,
          latitude,
          longitude,
          address,
          image,
          averageReviewScore,
          reviewCount,
          distance,
          bestOffer: {
            boardDetails,
            name,
            pointsToBeEarned,
            originalPrice: originalPrice,
            discountedPrice: discountedAmount,
            total: Number(total),
            totalInCurrency: total,
            currency,
            hasDiscount: discountedAmount > 0,
          },
        };
      })
      .filter((result) => result !== null)
  ) as SearchResultRow[];
};

const sortGroup = (offersRow: SearchResultRow[]) => {
  return offersRow.sort((a, b) => {
    const averageReviewScoreA = a.averageReviewScore ?? 0;
    const averageReviewScoreB = b.averageReviewScore ?? 0;

    if (averageReviewScoreB !== averageReviewScoreA) {
      return averageReviewScoreB - averageReviewScoreA;
    }

    return b.hotelStars - a.hotelStars;
  });
};

export const sortMostRelevantHotels = (offersRow: SearchResultRow[]) => {
  const [first, second] = offersRow.reduce<
    [SearchResultRow[], SearchResultRow[]]
  >(
    ([firstGroup, secondGroup], current) => {
      if (current.hotelStars >= 4) {
        return [[...firstGroup, current], secondGroup];
      }

      return [firstGroup, [...secondGroup, current]];
    },
    [[], []]
  );

  return [...sortGroup(first), ...sortGroup(second)];
};

export const handleFilters = async (
  filters: Filters,
  transformedData: SearchResultRow[],
  rate: number,
  lastHotelId?: string
  // _setOffersHotelDetails: any,
  // _getHotelDetail: any
): Promise<SearchResultRow[]> => {
  let filtered = transformedData;

  if (filters.priceRange) {
    filtered = filtered.filter(
      (data) =>
        data.bestOffer.total / rate >= filters.priceRange![0] &&
        data.bestOffer.total / rate <= filters.priceRange![1]
    );
  }

  // const hasActiveStarRatingFilter = Object.values(
  //   filters.hotelStarRatings
  // ).some((value) => value);
  // if (hasActiveStarRatingFilter) {
  //   filtered = filtered.filter(
  //     (data) => filters.hotelStarRatings[data.hotelStars]
  //   );
  // }

  const hasActiveStarRatingFilter = Object.values(
    filters.hotelStarRatings
  ).some((value) => value);

  if (hasActiveStarRatingFilter) {
    const selectedStarRatings = Object.keys(filters.hotelStarRatings)
      .filter((key) => filters.hotelStarRatings[key])
      .map(Number);

    filtered = filtered.filter((data) =>
      selectedStarRatings.includes(data.hotelStars)
    );
  }

  const hasActiveAmenitiesFilter = Object.values(filters.amenities).some(
    (value) => value
  );
  if (hasActiveAmenitiesFilter) {
    Object.entries(filters.amenities).forEach(([amenity, value]) => {
      if (value) {
        filtered = filtered.filter(
          (data) => data[amenity as keyof SearchResultRow]
        );
      }
    });
  }

  if (filters.selectedRating > 0) {
    filtered = filtered.filter(
      (data) => data.ratings >= filters.selectedRating
    );
  }

  if (filters.searchValue) {
    filtered = filtered.filter((data) =>
      data?.hotelDetail?.name
        .toLowerCase()
        .includes((filters.searchValue ?? "").toLowerCase())
    );
  }

  // const length = filtered.length > 10 ? 10 : filtered?.length;
  // const promises = [];

  // for (let i = 0; i < length; i++) {
  //   promises.push(_getHotelDetail(filtered[i]?.giataId));
  // }

  // const mapping: any = {};

  // Promise.all(promises).then((values) => {
  //   for (let i = 0; i < length; i++) {
  //     const providerHotelId = filtered[i].providerHotelId || "";
  //     mapping[providerHotelId] = values[i] || {};
  //   }

  //   _setOffersHotelDetails((offersHotelDetails: any) => ({
  //     ...offersHotelDetails,
  //     ...mapping,
  //   }));
  // });

  if (lastHotelId) {
    const index = filtered.findIndex(
      (result) => result.giataId === lastHotelId
    );
    if (index > -1) {
      const [topHotel] = filtered.splice(index, 1);
      filtered.unshift(topHotel);
    }
  }

  return filtered;
};

export const handleSorting = (
  t: ReturnType<typeof useTranslations>,
  sortingName: string,
  transformedData: SearchResultRow[]
): SearchResultRow[] => {
  const sortingData = getDropDownItems(t).find(
    (item) => item.name === sortingName
  );

  if (!sortingData) {
    return transformedData;
  }

  switch (sortingData.key) {
    case "price_asc":
      return [...transformedData].sort(
        (a, b) => a.bestOffer.total - b.bestOffer.total
      );
    case "price_desc":
      return [...transformedData].sort(
        (a, b) => b.bestOffer.total - a.bestOffer.total
      );
    case "starts_desc":
      return [...transformedData].sort((a, b) => b?.hotelStars - a?.hotelStars);
    case "starts_asc":
      return [...transformedData].sort((a, b) => a?.hotelStars - b?.hotelStars);
    case "recommended":
      return sortMostRelevantHotels([...transformedData]);
    default:
      return transformedData;
  }
};

export const getAllHotelLocations = (data: SearchResultRow[]) => {
  const allHotelLocations =
    data &&
    data?.map((item) => {
      return {
        latitude: Number(item.latitude),
        longitude: Number(item?.longitude),
      };
    });
  return allHotelLocations || [];
};

export const getAllHotelPrices = (
  data: SearchResultRow[],
  rate: number,
  currency: string
) => {
  const allHotelPrices =
    data &&
    data?.map(
      (item) =>
        `${mapCurrencyRounding(item?.bestOffer?.total / rate, currency)} ${currency}`
    );
  return allHotelPrices || [];
};

const BEST_IMAGE_SIZE = 800;

export const transformImages = (images: Image[]): Image[] => {
  return images.map((image: any) => {
    let selectedUrl = image.urls.find(
      (urlObj: any) => urlObj.maxWidth === BEST_IMAGE_SIZE
    );

    if (!selectedUrl) {
      selectedUrl = image.urls.find(
        (urlObj: any) => urlObj.maxWidth > BEST_IMAGE_SIZE
      );

      if (!selectedUrl) {
        selectedUrl =
          image.urls.find((urlObj: any) => urlObj.maxWidth === null) ||
          image.urls[image.urls.length - 1];
      }
    }

    return {
      ...image,
      urls: selectedUrl ? [selectedUrl] : [],
    };
  });
};
